/* eslint class-methods-use-this: "off" */
import { ParseTree } from 'antlr4ts/tree/ParseTree';
import { ErrorNode } from 'antlr4ts/tree/ErrorNode';
import { randomUUID } from 'node:crypto';
import { set } from 'lodash';
import {
  ElementDefinitionType as ElementDefinitionTypeSTU3,
  StructureDefinition as StructureDefinitionSTU3,
  StructureMap as StructureMapSTU3,
  StructureMapGroup as StructureMapGroupSTU3,
  StructureMapGroupRule as StructureMapGroupRuleSTU3,
  StructureMapGroupRuleSource as StructureMapGroupRuleSourceSTU3,
  StructureMapGroupRuleTarget as StructureMapGroupRuleTargetSTU3,
  StructureMapStructure as StructureMapStructureSTU3,
  StructureMapGroupRuleTargetParameter as StructureMapGroupRuleTargetParameterSTU3,
} from 'fhir/r3';
import {
  ElementDefinitionType as ElementDefinitionTypeR4,
  StructureDefinition as StructureDefinitionR4,
  StructureMap as StructureMapR4,
  StructureMapGroup as StructureMapGroupR4,
  StructureMapGroupRule as StructureMapGroupRuleR4,
  StructureMapGroupRuleSource as StructureMapGroupRuleSourceR4,
  StructureMapGroupRuleTarget as StructureMapGroupRuleTargetR4,
  StructureMapStructure as StructureMapStructureR4,
  StructureMapGroupRuleTargetParameter as StructureMapGroupRuleTargetParameterR4,
} from 'fhir/r4';
import {
  ElementDefinitionType as ElementDefinitionTypeR5,
  StructureDefinition as StructureDefinitionR5,
  StructureMap as StructureMapR5,
  StructureMapGroup as StructureMapGroupR5,
  StructureMapGroupRule as StructureMapGroupRuleR5,
  StructureMapGroupRuleSource as StructureMapGroupRuleSourceR5,
  StructureMapGroupRuleTarget as StructureMapGroupRuleTargetR5,
  StructureMapStructure as StructureMapStructureR5,
  StructureMapGroupRuleTargetParameter as StructureMapGroupRuleTargetParameterR5,
} from 'fhir/r5';
import {
  StructureMapContext,
  GroupContext,
  RuleDefinitionContext,
  MapIdContext,
  StructureContext,
  RuleSourcesContext,
  RuleTargetsContext,
  MetadataContext,
  MetadataValueContext,
  LiteralContext,
  RuleNameContext,
  SourceDefaultContext,
  RuleSourceContext,
  ImportsContext,
} from './generated/FhirMapperParser';
import { FhirMapperVisitor } from './generated/FhirMapperVisitor';
import { FhirPrimitiveValue, FhirVersion } from './types';

type Overwrite<T, U> = Omit<T, keyof U> & U;

type ElementDefinitionType =
  | ElementDefinitionTypeSTU3
  | ElementDefinitionTypeR4
  | ElementDefinitionTypeR5;
type StructureDefinition = StructureDefinitionSTU3 | StructureDefinitionR4 | StructureDefinitionR5;
type StructureMap = StructureMapSTU3 | StructureMapR4 | StructureMapR5;
type StructureMapGroup = StructureMapGroupSTU3 | StructureMapGroupR4 | StructureMapGroupR5;
type StructureMapGroupRule =
  | StructureMapGroupRuleSTU3
  | StructureMapGroupRuleR4
  | StructureMapGroupRuleR5;
type StructureMapGroupRuleSource =
  | StructureMapGroupRuleSourceSTU3
  | StructureMapGroupRuleSourceR4
  | StructureMapGroupRuleSourceR5;
type StructureMapGroupRuleTargetParameter =
    | StructureMapGroupRuleTargetParameterSTU3
    | StructureMapGroupRuleTargetParameterR4
    | StructureMapGroupRuleTargetParameterR5;
type StructureMapGroupRuleTarget =
  | Overwrite<
  StructureMapGroupRuleTargetSTU3,
  { parameter?: StructureMapGroupRuleTargetParameter[] }
  >
  | Overwrite<
  StructureMapGroupRuleTargetR4,
  { parameter?: StructureMapGroupRuleTargetParameter[] }
  >
  | Overwrite<
  StructureMapGroupRuleTargetR5,
  { parameter?: StructureMapGroupRuleTargetParameter[] }
  >;
type StructureMapStructure =
  | StructureMapStructureSTU3
  | StructureMapStructureR4
  | StructureMapStructureR5;

type GroupTypeMode = 'none' | 'types' | 'type-and-types';

const structureTypeModes = ['source', 'queried', 'target', 'produced'] as const;
type StructureTypeMode = typeof structureTypeModes[number];

const inputModes = ['source', 'target'] as const;
type InputMode = typeof inputModes[number];

/**
 * The `StructureMapVisitor` class iterates over the token parsed from the FML and returns the
 * fragment of the StructureMap object to which the token pertains. It is an extension of the
 * automatically-generated FhirMapperVisitor.
 */
export abstract class StructureMapVisitor implements FhirMapperVisitor<any> {
  readonly METADATA_KEYS: Array<keyof StructureMap> = [
    'contact',
    'copyright',
    'date',
    'description',
    'experimental',
    'id',
    'identifier',
    'jurisdiction',
    'name',
    'publisher',
    'purpose',
    'status',
    'title',
    'url',
    'useContext',
    'version',
  ] as const;

  protected structureMapDefinition!: StructureDefinition;

  protected fhirTypes!: Partial<StructureDefinition>[];

  protected fhirVersion!: FhirVersion;

  visit(tree: ParseTree): any {
    return tree.accept(this);
  }

  visitTerminal() { }

  visitErrorNode(node: ErrorNode) {
    // dunno what this does yet...
    throw new Error(node.text);
  }

  visitStructureMap(ctx: StructureMapContext): StructureMap {
    const metadata: any = {};
    const metadataVisitResult = ctx.metadata().map((m) => m.accept(this));

    metadataVisitResult.forEach((r) => {
      Object.assign(metadata, r);
    });

    const mapId = ctx.mapId().accept(this);
    const structures = ctx.structure().map((s) => s.accept(this));
    const imports = ctx.imports().map((i) => i.accept(this));
    const groups = ctx.group().map((g) => g.accept(this));

    return {
      resourceType: 'StructureMap',
      url: mapId.url,
      name: mapId.name,
      // "draft" is the default value (https://www.hl7.org/fhir/mapping-language.html#metadata)
      status: 'draft',
      structure: structures,
      import: imports,
      group: groups,
      ...metadata,
    };
  }

  /**
   * Returns a typed value from the inbound metadata field, according to the type expected by the
   * StructureMap FHIR profile
   * @param type The primitive type from the FHIR profile that this field is allowed to be.
   * @param parsedValue The value from the metadata field.
   * @returns
   */
  private getPrimitiveValue(
    type: ElementDefinitionType,
    parsedValue?: MetadataValueContext,
  ): string | boolean | number | undefined {
    if (!parsedValue) return undefined;

    switch (type.code) {
      case 'boolean':
        if (parsedValue.text === 'false') return false;
        if (parsedValue.text === 'true') return true;
        return undefined;
      case 'string':
      case 'code':
        return this.sanitiseString((parsedValue.literal() || parsedValue.markdown())?.text);
      default:
        return undefined;
    }
  }

  visitMetadata(ctx: MetadataContext): Partial<StructureDefinition> {
    const partialStructureDef: Partial<StructureDefinition> = {};

    const key = ctx.metadataKey().text;

    if (!this.METADATA_KEYS.includes(key.split('.')[0] as keyof StructureMap)) return partialStructureDef;

    const value = ctx.metadataValue();
    const elemDef = this.structureMapDefinition.snapshot?.element.find((e) => e.path === `${this.structureMapDefinition.name}.${key}`);

    if (!value && !elemDef) return partialStructureDef;

    const targetTypes = this.fhirTypes
      .filter((ft) => elemDef?.type?.map((et) => et.code).includes(ft.id!));

    const typedValues: any[] = [];

    targetTypes.forEach((t) => {
      const currentElemDef = elemDef!.type!.find((type) => type.code === t.id)!;
      if (t.kind === 'primitive-type') {
        typedValues.push(this.getPrimitiveValue(currentElemDef, value));
      } else if (t.kind === 'complex-type') {
        console.warn('Assigning FML metadata to complex FHIR types is not yet supported.');
      }
    });

    /**
     * None of the elements that are assignable outside of the main body of the can be of multiple
     * types, so this should always assign the correct value.
     */
    if (typedValues.length === 1) set(partialStructureDef, key, typedValues[0]);

    return partialStructureDef;
  }

  visitMapId(ctx: MapIdContext): Pick<StructureMap, 'url' | 'name'> {
    const url = ctx.url().text.replace(/"/g, '');
    const alias = ctx.mapAlias()?.variableId().text.replace(/^"|"$/g, '') ?? 'Unnamed';
    return { url, name: alias };
  }

  visitStructure(ctx: StructureContext): StructureMapStructure {
    const mode = ctx.modelMode().text as StructureTypeMode;

    if (!structureTypeModes.includes(mode)) {
      throw new Error(`Invalid structure type mode: ${mode}. Structure type mode must be one of: ${structureTypeModes.join(', ')}.`);
    }

    return {
      url: ctx.url().text.replace(/"/g, ''),
      alias: ctx.structureAlias()?.variableId()?.text,
      mode,
    };
  }

  visitImports(ctx: ImportsContext): string {
    return ctx.url().text.replace(/"/g, '');
  }

  visitGroup(ctx: GroupContext): StructureMapGroup {
    const groupName = ctx.variableId().text;
    const inputs = ctx.parameters().parameter().map((p) => {
      const mode = p.inputMode().text as InputMode;

      if (!inputModes.includes(mode)) {
        throw new Error(`Invalid input mode: ${mode}. Input mode must be one of: ${inputModes.join(', ')}.`);
      }

      const name = p.variableId().text;
      const type = p.type()?.variableId().text ?? undefined;
      return { name, mode, type };
    });

    const rules = ctx.rules().ruleDefinition().map((rule) => rule.accept(this));

    let typeMode: GroupTypeMode | undefined;
    const fmlTypeMode = ctx.typeMode()?.groupTypeMode().text;

    switch (fmlTypeMode) {
      case 'types':
        typeMode = 'types';
        break;
      case 'type+':
        typeMode = 'type-and-types';
        break;
      default:
        if (this.fhirVersion !== 'R5') {
          typeMode = 'none';
        }
        break;
    }

    return {
      name: groupName,
      typeMode,
      input: inputs,
      rule: rules,
    };
  }

  protected getRuleName(ctx?: RuleNameContext, ruleSource?: StructureMapGroupRuleSource): string {
    const declaredName = ctx?.variableId().text.replace(/^"|"$/g, '');

    if (declaredName) {
      return declaredName;
    }

    let constructedName: string | undefined;

    if (ruleSource) {
      constructedName = ruleSource.element;
      if (ruleSource.type) {
        constructedName = `${constructedName}-${ruleSource.type}`;
      }
    }

    return constructedName || randomUUID();
  }

  abstract visitRuleDefinition(ctx: RuleDefinitionContext): StructureMapGroupRule;

  abstract getDefaultValue(ctx: SourceDefaultContext): { key: string, value: string };

  protected getBaseRuleSource(ctx: RuleSourceContext): StructureMapGroupRuleSource {
    const ruleCtx = ctx.ruleCtx().text.split('.');
    const context = ruleCtx[0];
    const element = ruleCtx[1] ?? undefined;
    const type = ctx.sourceType()?.variableId().text;
    const cardinalityMin = ctx.sourceCardinality()?.INTEGER().text;
    const min = cardinalityMin ? Number(cardinalityMin) : undefined;
    const max = ctx.sourceCardinality()?.upperBound().text;
    const condition = ctx.whereClause()?.fhirPath().text;
    const variable = ctx.alias()?.variableId().text;
    let defaultValue: string | undefined;
    let defaultKey: string | undefined;

    if (ctx.sourceDefault()) {
      ({ key: defaultKey, value: defaultValue } = this.getDefaultValue(ctx.sourceDefault()!));
    }

    const ruleSource: StructureMapGroupRuleSource = {
      context,
      element,
      type,
      min,
      max,
      variable,
      condition,
    };

    if (defaultValue) {
      // @ts-ignore the key will be valid, because of our implementation
      ruleSource[defaultKey] = defaultValue;
    }

    return ruleSource;
  }

  abstract visitRuleSources(ctx: RuleSourcesContext): StructureMapGroupRuleSource[];

  private sanitiseString(source?: string): string | undefined {
    if (!source) return undefined;
    return source.replace(/^'|'$/g, '');
  }

  /**
   * Converts the "literal" value from the FML to a JavaScript type and its accompanying named FHIR
   * Type
   * @param literal The parsed "literal" token from the FML
   * @returns The FHIR primitive type and the actual value from the token.
   */
  protected getValueFromLiteral(literal: LiteralContext): FhirPrimitiveValue | undefined {
    if (literal.BOOL()) {
      return {
        type: 'boolean',
        value: Boolean(literal.BOOL()!.text).valueOf(),
      };
    }

    if (literal.DATE()) {
      return {
        type: 'date',
        value: this.sanitiseString(literal.DATE()!.text),
      };
    }

    if (literal.DATETIME()) {
      return {
        type: 'dateTime',
        value: this.sanitiseString(literal.DATETIME()!.text),
      };
    }

    if (literal.INTEGER()) {
      return {
        type: 'integer',
        value: Number.parseInt(literal.INTEGER()!.text || 'NaN', 10),
      };
    }

    if (literal.NUMBER()) {
      return {
        type: 'decimal',
        value: Number.parseInt(literal.NUMBER()!.text || 'NaN', 10),
      };
    }

    if (literal.STRING()) {
      return {
        type: 'string',
        value: this.sanitiseString(literal.STRING()!.text),
      };
    }

    if (literal.TIME()) {
      return {
        type: 'time',
        value: this.sanitiseString(literal.TIME()!.text),
      };
    }

    return undefined;
  }

  visitRuleTargets(ctx: RuleTargetsContext): StructureMapGroupRuleTarget[] {
    return ctx.ruleTarget().map((tgt) => {
      const ruleCtx = tgt.ruleCtx()?.text.split('.');

      if (!ruleCtx) return null;

      const targetTransform = tgt.transform();
      const evaluateTransform = targetTransform?.evaluateTransform();
      const namedInvocation = targetTransform?.namedInvocation();
      let transform: string | undefined;
      const parameter: StructureMapGroupRuleTargetParameter[] = [];

      if (evaluateTransform) {
        transform = 'evaluate';
        parameter.push({
          valueId: evaluateTransform.variableId()?.text,
        }, {
          valueString: evaluateTransform.fhirPath()?.text,
        });
      } else if (namedInvocation) {
        transform = namedInvocation?.variableId().text;

        if (namedInvocation.paramList()) {
          namedInvocation.paramList()?.param().forEach((p) => {
            let value: FhirPrimitiveValue | undefined;

            if (p.literal()) {
              value = this.getValueFromLiteral(p.literal()!);
            } else if (p.variableId()) {
              value = {
                value: p.variableId()!.text,
                type: 'id',
              };
            }

            if (value) {
              parameter.push({
                [`value${value.type[0].toUpperCase()}${value.type.substring(1)}`]: value.value,
              });
            }
          });
        }
      } else if (targetTransform?.literal() || targetTransform?.ruleCtx()) {
        transform = 'copy';

        if (targetTransform.literal()) {
          const value = this.getValueFromLiteral(targetTransform.literal()!);

          if (value) {
            parameter.push({
              [`value${value.type[0].toUpperCase()}${value.type.substring(1)}`]: value.value,
            });
          }
        } else if (targetTransform.ruleCtx()) {
          parameter.push({
            valueId: targetTransform?.ruleCtx()?.text,
          });
        }
      }

      const variable = tgt.alias()?.variableId().text;

      const mappingRuleTarget: StructureMapGroupRuleTarget = {
        context: ruleCtx[0],
        element: ruleCtx[1] ?? undefined,
      };

      if (transform) {
        mappingRuleTarget.transform = transform as StructureMapGroupRuleTarget['transform'];
      }

      if (variable) {
        mappingRuleTarget.variable = variable;
      }

      if (parameter.length) {
        mappingRuleTarget.parameter = parameter;
      }

      return mappingRuleTarget;
    }).filter((item): item is StructureMapGroupRuleTarget => Boolean(item));
  }

  // Default method fallback
  visitChildren(): any {

  }
}
